import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  BadgeCheck,
  Landmark,
  CreditCard,
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Eye,
  ExternalLink,
  Building2,
  Hash,
  MapPin,
  Stethoscope,
  Star,
  Activity,
  ClipboardList,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getRoleLabel, getRoleColor } from "@/utils/roleUtils";

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_STYLES = {
  registered: "bg-slate-100 text-slate-700 border-slate-200",
  documents_uploaded: "bg-amber-100 text-amber-700 border-amber-200",
  under_review: "bg-blue-100 text-blue-700 border-blue-200",
  approved: "bg-purple-100 text-purple-700 border-purple-200",
  payment_pending: "bg-orange-100 text-orange-700 border-orange-200",
  enrolled: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  qualified: "bg-emerald-100 text-emerald-700 border-emerald-200",
  call_booking_sent: "bg-sky-100 text-sky-700 border-sky-200",
  call_booked: "bg-indigo-100 text-indigo-700 border-indigo-200",
  interview_completed: "bg-violet-100 text-violet-700 border-violet-200",
  paid_in_full: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  paid: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
  pending_validation: "bg-amber-100 text-amber-700 border-amber-200",
  pending_approval: "bg-orange-100 text-orange-700 border-orange-200",
  payable: "bg-blue-100 text-blue-700 border-blue-200"
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    {(status || "—").replace(/_/g, " ")}
  </span>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, color = "amber", children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const colorMap = {
    amber: "text-amber-600 bg-amber-50",
    blue: "text-blue-600 bg-blue-50",
    green: "text-green-600 bg-green-50",
    purple: "text-purple-600 bg-purple-50",
    red: "text-red-600 bg-red-50",
    slate: "text-slate-600 bg-slate-50"
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className={`flex items-center justify-between px-6 py-4 border-b border-slate-100 ${collapsible ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorMap[color]}`}>
            <Icon size={18} />
          </div>
          <h2 className="font-semibold text-slate-900">{title}</h2>
        </div>
        {collapsible && (
          <button className="text-slate-400 hover:text-slate-600">
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}
      </div>
      {open && <div className="p-6">{children}</div>}
    </div>
  );
};

// ─── Info Row ─────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, mono = false, icon: Icon }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-b-0">
    {Icon && <Icon size={15} className="text-slate-400 mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      <p className={`text-sm font-medium text-slate-900 truncate ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-slate-400 font-normal italic">Not provided</span>}
      </p>
    </div>
  </div>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color = "slate", icon: Icon }) => {
  const colorMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    green: "bg-green-50 border-green-200 text-green-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    red: "bg-red-50 border-red-200 text-red-700"
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className="opacity-70" />}
        <p className="text-xs font-medium opacity-70 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-1">{sub}</p>}
    </div>
  );
};

// ─── Bank Details Editor ──────────────────────────────────────────────────────

const BankDetailsEditor = ({ userId, initialBankDetails, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialBankDetails || {});

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/admin/users/${userId}/profile/bank-details`, {
        bank_details: form
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bank details saved");
      setEditing(false);
      onSaved(form);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bank details");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "account_holder_name", label: "Account Holder Name", placeholder: "Full name on account" },
    { key: "sort_code", label: "Sort Code", placeholder: "12-34-56", maxLength: 8 },
    { key: "account_number", label: "Account Number", placeholder: "12345678", maxLength: 8 },
    { key: "bank_name", label: "Bank Name", placeholder: "e.g. HDFC, ICICI, SBI" },
    { key: "ifsc_code", label: "IFSC Code", placeholder: "e.g. HDFC0001234" },
    { key: "upi_id", label: "UPI ID", placeholder: "phone@upi (optional)" }
  ];

  if (!editing) {
    const bd = initialBankDetails;
    const hasBankDetails = bd && (bd.account_holder_name || bd.account_number || bd.ifsc_code);
    return (
      <div>
        {hasBankDetails ? (
          <div className="space-y-0">
            <InfoRow label="Account Holder" value={bd.account_holder_name} icon={User} />
            {bd.sort_code && <InfoRow label="Sort Code" value={bd.sort_code} mono icon={Hash} />}
            <InfoRow label="Account Number" value={bd.account_number} mono icon={Hash} />
            {bd.bank_name && <InfoRow label="Bank Name" value={bd.bank_name} icon={Building2} />}
            {bd.ifsc_code && <InfoRow label="IFSC Code" value={bd.ifsc_code} mono icon={Hash} />}
            {bd.upi_id && <InfoRow label="UPI ID" value={bd.upi_id} mono icon={Landmark} />}
          </div>
        ) : (
          <div className="flex items-center gap-3 py-4 text-slate-500">
            <AlertCircle size={16} className="text-amber-400" />
            <p className="text-sm">No bank details on file.</p>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={() => { setForm(initialBankDetails || {}); setEditing(true); }}
        >
          <Edit2 size={14} className="mr-1.5" />
          {hasBankDetails ? "Edit Bank Details" : "Add Bank Details"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(({ key, label, placeholder, maxLength }) => (
          <div key={key}>
            <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
            <Input
              value={form[key] || ""}
              onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              placeholder={placeholder}
              maxLength={maxLength}
              className="text-sm"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
          Save
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={saving}
        >
          <X size={14} className="mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
};

// ─── Commissions Table ────────────────────────────────────────────────────────

const CommissionsTable = ({ commissions }) => {
  if (!commissions || commissions.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <DollarSign size={32} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No commissions yet</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Student</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Paid At</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {commissions.map((c, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-3 font-medium text-slate-900">{c.student_name}</td>
              <td className="px-6 py-3">
                <span className="font-semibold text-green-700">
                  £{parseFloat(c.commission_amount_gbp || 0).toFixed(2)}
                </span>
              </td>
              <td className="px-6 py-3"><StatusBadge status={c.status} /></td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Payments Table ───────────────────────────────────────────────────────────

const PaymentsTable = ({ payments }) => {
  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <CreditCard size={32} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No payments recorded</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Method</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Ref</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {payments.map((p, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-3 font-semibold text-slate-900">
                £{parseFloat(p.amount_gbp || 0).toFixed(2)}
              </td>
              <td className="px-6 py-3 text-slate-600">{(p.payment_type || "—").replace(/_/g, " ")}</td>
              <td className="px-6 py-3 text-slate-600">{(p.payment_method || "—").replace(/_/g, " ")}</td>
              <td className="px-6 py-3"><StatusBadge status={p.status} /></td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : (p.created_at ? new Date(p.created_at).toLocaleDateString() : "—")}
              </td>
              <td className="px-6 py-3 text-slate-500 text-xs font-mono">{p.reference_number || p.stripe_payment_intent_id?.slice(-8) || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Students Table ───────────────────────────────────────────────────────────

const StudentsTable = ({ students }) => {
  if (!students || students.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Users size={32} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No students registered</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Student</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Paid (£)</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Enrolled</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Registered</th>
            <th className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {students.map((s, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-3">
                <p className="font-medium text-slate-900">{s.student_name}</p>
                <p className="text-slate-500 text-xs">{s.student_email}</p>
              </td>
              <td className="px-6 py-3"><StatusBadge status={s.status} /></td>
              <td className="px-6 py-3 font-semibold text-green-700">
                £{parseFloat(s.total_paid_gbp || 0).toFixed(2)}
              </td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-6 py-3">
                <Link to={`/portal/admin/applications/${s.student_id}`}>
                  <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                    <Eye size={14} className="mr-1" /> View
                  </Button>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Reps Table ───────────────────────────────────────────────────────────────

const RepsTable = ({ reps }) => {
  if (!reps || reps.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Users size={32} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No reps assigned</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Rep</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Students</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Commission Paid</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Bank Details</th>
            <th className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {reps.map((r, i) => {
            const bd = r.bank_details;
            const hasBankDetails = bd && (bd.account_holder_name || bd.account_number);
            return (
              <tr key={i} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <p className="font-medium text-slate-900">{r.name}</p>
                  <p className="text-slate-500 text-xs">{r.email}</p>
                  {r.phone && <p className="text-slate-400 text-xs">{r.phone}</p>}
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(r.role).bg} ${getRoleColor(r.role).text}`}>
                    {getRoleLabel(r.role)}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-3 text-center">
                  <span className="font-semibold text-blue-700">{r.total_students}</span>
                  <span className="text-slate-400 text-xs"> ({r.enrolled_students} enrolled)</span>
                </td>
                <td className="px-6 py-3 font-semibold text-green-700">
                  £{parseFloat(r.total_commission_paid_gbp || 0).toFixed(2)}
                </td>
                <td className="px-6 py-3">
                  {hasBankDetails ? (
                    <div className="text-xs text-slate-600">
                      <p className="font-medium">{bd.account_holder_name}</p>
                      <p className="font-mono text-slate-500">A/C: {bd.account_number}</p>
                      {bd.ifsc_code && <p className="font-mono text-slate-400">IFSC: {bd.ifsc_code}</p>}
                    </div>
                  ) : (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle size={12} /> Missing
                    </span>
                  )}
                </td>
                <td className="px-6 py-3">
                  <Link to={`/portal/admin/users/${r.user_id}/profile`}>
                    <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Documents Table ──────────────────────────────────────────────────────────
const DocumentsTable = ({ documents }) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <FileText size={32} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No documents uploaded</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Document</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Verified</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Comment</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {documents.map((d, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-6 py-3 font-medium text-slate-900 capitalize">
                {(d.doc_type || "—").replace(/_/g, " ")}
              </td>
              <td className="px-6 py-3"><StatusBadge status={d.status} /></td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-6 py-3 text-slate-500 text-xs">
                {d.verified_at ? new Date(d.verified_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-6 py-3 text-xs text-slate-500 max-w-xs truncate">{d.admin_comment || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Profile Page ────────────────────────────────────────────────────────

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setBankDetails(res.data.bank_details);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load user profile");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="animate-spin h-10 w-10 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-20">
        <User size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">User not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const { extra = {} } = profile;
  const role = profile.role;

  const roleColorClass = getRoleColor(role);

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-slate-600">
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
      </div>

      {/* Hero Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-slate-800 to-slate-700" />
        <div className="px-8 pb-6 -mt-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className={`w-20 h-20 rounded-2xl border-4 border-white flex items-center justify-center text-white text-2xl font-bold shadow-md ${roleColorClass.badge}`}>
              {profile.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 pb-1">
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <h1 className="text-2xl font-bold text-slate-900">{profile.name}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${roleColorClass.bg} ${roleColorClass.text}`}>
                  {getRoleLabel(role)}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${profile.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                  {profile.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 mt-2">
                <span className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Mail size={14} className="text-slate-400" />{profile.email}
                </span>
                {profile.phone && (
                  <span className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Phone size={14} className="text-slate-400" />{profile.phone}
                  </span>
                )}
                {profile.created_at && (
                  <span className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Calendar size={14} className="text-slate-400" />
                    Joined {new Date(profile.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
                  </span>
                )}
                {profile.last_login_at && (
                  <span className="flex items-center gap-1.5 text-sm text-slate-500">
                    <Activity size={14} className="text-slate-400" />
                    Last login {new Date(profile.last_login_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/portal/admin/users`}>
                <Button variant="outline" size="sm">
                  <Users size={14} className="mr-1.5" /> All Users
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ─── STUDENT VIEW ─────────────────────────────────────────────────── */}
      {role === "student" && extra.student && (
        <>
          {/* Student Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              label="Status"
              value={(extra.student.status || "—").replace(/_/g, " ")}
              icon={BadgeCheck}
              color="blue"
            />
            <StatCard
              label="Total Paid"
              value={`£${parseFloat(extra.total_paid_gbp || 0).toFixed(2)}`}
              icon={CreditCard}
              color="green"
            />
            <StatCard
              label="Balance"
              value={`£${parseFloat(extra.balance_gbp || 0).toFixed(2)}`}
              icon={TrendingUp}
              color={extra.balance_gbp > 0 ? "amber" : "green"}
            />
            <StatCard
              label="Documents"
              value={extra.documents?.length || 0}
              sub={`${extra.documents?.filter(d => d.status === "verified").length || 0} verified`}
              icon={FileText}
              color="purple"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Personal Info */}
            <Section title="Personal Information" icon={User} color="blue">
              <InfoRow label="Full Name" value={profile.name} icon={User} />
              <InfoRow label="Email" value={profile.email} icon={Mail} />
              <InfoRow label="Phone" value={profile.phone} icon={Phone} />
              <InfoRow label="WhatsApp" value={extra.student.whatsapp_number} icon={Phone} />
              <InfoRow label="Date of Birth" value={extra.student.dob ? new Date(extra.student.dob).toLocaleDateString() : null} icon={Calendar} />
              <InfoRow label="City" value={extra.student.city} icon={MapPin} />
              <InfoRow label="State" value={extra.student.state} icon={MapPin} />
            </Section>

            {/* Academic Info */}
            <Section title="Academic & Professional" icon={Stethoscope} color="purple">
              <InfoRow label="Dental Reg. Number" value={extra.student.dental_reg_number} mono icon={Hash} />
              <InfoRow label="Experience" value={extra.student.experience_years ? `${extra.student.experience_years} years` : null} icon={Star} />
              <InfoRow label="Course ID" value={extra.student.course_id} icon={BookOpen} />
              <InfoRow label="Enrollment Number" value={extra.student.enrollment_number} mono icon={Hash} />
              <InfoRow label="Registration Source" value={extra.student.registration_source} icon={ClipboardList} />
              <InfoRow label="Student ID" value={extra.student.student_id} mono icon={Hash} />
              {extra.rep && (
                <InfoRow label="Enrolled by (Rep)" value={`${extra.rep.name} (${extra.rep.email})`} icon={User} />
              )}
            </Section>
          </div>

          {/* Payments */}
          <Section title="Payment History" icon={CreditCard} color="green" collapsible defaultOpen={true}>
            <PaymentsTable payments={extra.payments} />
          </Section>

          {/* Documents */}
          <Section title="Documents" icon={FileText} color="purple" collapsible defaultOpen={true}>
            <DocumentsTable documents={extra.documents} />
          </Section>

          {/* Feedback */}
          {extra.student.admin_feedback && (
            <Section title="Admin Feedback" icon={AlertCircle} color="red">
              <p className="text-sm text-slate-700 leading-relaxed">{extra.student.admin_feedback}</p>
            </Section>
          )}
        </>
      )}

      {/* ─── REP / SALES_USER VIEW ────────────────────────────────────────── */}
      {["rep", "sales_user"].includes(role) && (
        <>
          {/* Rep Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Students" value={extra.student_counts?.total || 0} icon={Users} color="blue" />
            <StatCard label="Enrolled" value={extra.student_counts?.enrolled || 0} icon={CheckCircle} color="green" />
            <StatCard label="Qualified" value={extra.student_counts?.qualified || 0} icon={BadgeCheck} color="purple" />
            <StatCard label="Pending" value={extra.student_counts?.pending || 0} icon={Clock} color="amber" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Commission" value={`£${parseFloat(extra.commission_summary?.total_gbp || 0).toFixed(2)}`} icon={DollarSign} color="green" />
            <StatCard label="Paid Out" value={`£${parseFloat(extra.commission_summary?.paid_gbp || 0).toFixed(2)}`} icon={CheckCircle} color="green" />
            <StatCard label="Approved" value={`£${parseFloat(extra.commission_summary?.approved_gbp || 0).toFixed(2)}`} icon={BadgeCheck} color="blue" />
            <StatCard label="Pending" value={`£${parseFloat(extra.commission_summary?.pending_gbp || 0).toFixed(2)}`} icon={Clock} color="amber" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Contact Info */}
            <Section title="Contact Information" icon={User} color="blue">
              <InfoRow label="Full Name" value={profile.name} icon={User} />
              <InfoRow label="Email" value={profile.email} icon={Mail} />
              <InfoRow label="Phone" value={profile.phone} icon={Phone} />
              <InfoRow label="User ID" value={profile.user_id} mono icon={Hash} />
              {extra.manager && (
                <div className="py-2.5 border-b border-slate-50">
                  <p className="text-xs text-slate-500 mb-1">Manager</p>
                  <Link to={`/portal/admin/users/${extra.manager.user_id}/profile`} className="text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1">
                    {extra.manager.name} <ExternalLink size={12} />
                  </Link>
                  <p className="text-xs text-slate-500">{extra.manager.email}</p>
                </div>
              )}
            </Section>

            {/* Bank Details */}
            <Section title="Bank Account Details" icon={Landmark} color="green">
              <BankDetailsEditor
                userId={userId}
                initialBankDetails={bankDetails}
                onSaved={(bd) => setBankDetails(bd)}
              />
            </Section>
          </div>

          {/* Students */}
          <Section title="Registered Students" icon={Users} color="blue" collapsible defaultOpen={true}>
            <StudentsTable students={extra.students} />
          </Section>

          {/* Commissions */}
          <Section title="Commission History" icon={DollarSign} color="green" collapsible defaultOpen={true}>
            <CommissionsTable commissions={extra.commissions} />
          </Section>
        </>
      )}

      {/* ─── MANAGER VIEW ─────────────────────────────────────────────────── */}
      {role === "manager" && (
        <>
          {/* Manager Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Reps" value={extra.rep_stats?.total_reps || 0} icon={Users} color="blue" />
            <StatCard label="Active Reps" value={extra.rep_stats?.active_reps || 0} icon={CheckCircle} color="green" />
            <StatCard label="Total Commission" value={`£${parseFloat(extra.commission_summary?.total_gbp || 0).toFixed(2)}`} icon={DollarSign} color="amber" />
            <StatCard label="Commission Paid" value={`£${parseFloat(extra.commission_summary?.paid_gbp || 0).toFixed(2)}`} icon={TrendingUp} color="green" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Contact Info */}
            <Section title="Contact Information" icon={User} color="blue">
              <InfoRow label="Full Name" value={profile.name} icon={User} />
              <InfoRow label="Email" value={profile.email} icon={Mail} />
              <InfoRow label="Phone" value={profile.phone} icon={Phone} />
              <InfoRow label="User ID" value={profile.user_id} mono icon={Hash} />
              {extra.manager_record?.commission_override !== null && extra.manager_record?.commission_override !== undefined && (
                <InfoRow
                  label="Commission Override %"
                  value={`${(extra.manager_record.commission_override * 100).toFixed(1)}%`}
                  icon={TrendingUp}
                />
              )}
            </Section>

            {/* Bank Details */}
            <Section title="Bank Account Details" icon={Landmark} color="green">
              <BankDetailsEditor
                userId={userId}
                initialBankDetails={bankDetails}
                onSaved={(bd) => setBankDetails(bd)}
              />
            </Section>
          </div>

          {/* Reps under this manager */}
          <Section title="Reps / Team Members" icon={Users} color="blue" collapsible defaultOpen={true}>
            <RepsTable reps={extra.reps} />
          </Section>

          {/* Manager Commissions */}
          {extra.commissions && extra.commissions.length > 0 && (
            <Section title="Commission History" icon={DollarSign} color="green" collapsible defaultOpen={false}>
              <CommissionsTable commissions={extra.commissions} />
            </Section>
          )}
        </>
      )}

      {/* ─── ADMIN VIEW ───────────────────────────────────────────────────── */}
      {["admin", "super_admin"].includes(role) && (
        <>
          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Account Details" icon={Shield} color="red">
              <InfoRow label="Full Name" value={profile.name} icon={User} />
              <InfoRow label="Email" value={profile.email} icon={Mail} />
              <InfoRow label="Phone" value={profile.phone} icon={Phone} />
              <InfoRow label="User ID" value={profile.user_id} mono icon={Hash} />
              <InfoRow label="Role" value={getRoleLabel(role)} icon={Shield} />
              <InfoRow label="2FA Enabled" value={profile.two_factor_enabled ? "Yes" : "No"} icon={BadgeCheck} />
              {profile.last_login_at && (
                <InfoRow label="Last Login" value={new Date(profile.last_login_at).toLocaleString()} icon={Activity} />
              )}
              {profile.last_login_ip && (
                <InfoRow label="Last Login IP" value={profile.last_login_ip} mono icon={Shield} />
              )}
            </Section>

            <Section title="Bank Account Details" icon={Landmark} color="green">
              <BankDetailsEditor
                userId={userId}
                initialBankDetails={bankDetails}
                onSaved={(bd) => setBankDetails(bd)}
              />
            </Section>
          </div>

          {extra.recent_audit_logs && extra.recent_audit_logs.length > 0 && (
            <Section title="Recent Audit Activity" icon={ClipboardList} color="slate" collapsible defaultOpen={true}>
              <div className="overflow-x-auto -mx-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-y border-slate-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Object</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {extra.recent_audit_logs.map((log, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-6 py-3 text-xs font-mono text-slate-700">{log.action_type}</td>
                        <td className="px-6 py-3 text-xs text-slate-600">{log.object_type}</td>
                        <td className="px-6 py-3 text-xs text-slate-500 max-w-sm truncate">{log.description}</td>
                        <td className="px-6 py-3 text-xs text-slate-400">
                          {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default UserProfilePage;
